import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Controller,
  Delimiter,
  Input,
  Typography,
  useForm,
} from '@otp/ui';
import { Rule } from '@otp/icons';
import { AddLoginCriteriaButton } from './login-rule-criteria/add-new-criteria-button';
import {
  Form,
  LoginRuleCriteria,
  LoginRuleCriteriaType,
  SelectedRole,
} from 'src/components/login-rules/form/types';
import { LoginRuleCriteriaTable } from 'src/components/login-rules/form/login-rule-criteria/table';
import { LoginRuleCriteriaAction } from 'src/components/login-rules/form/login-rule-criteria/table/columns/actions/actions-modals';
import { ContentWrapper } from 'src/components/common/wrapper/content/wrapper';
import { TableWrapper } from 'src/components/common/wrapper/table/wrapper';
import { RolesSection } from './roles';
import {
  CreateLoginRuleDocument,
  CreateLoginRuleDto,
  UpdateLoginRuleDocument,
  UpdateLoginRuleDto,
} from 'src/graphql/generated/graphql';
import { DocumentNode, useMutation } from '@apollo/client';
import { useLoginRule } from 'src/hooks/query/login-rules/use-login-rule';
import { mapFormDataToLoginRuleDto, mapLoginRuleToFormData } from './utils';
import { useNavigate } from 'react-router-dom';
import { Routes } from 'src/components/common/router/constants';

type LoginRuleFormProps = {
  onFinish?: () => void;
  id?: string;
  refetchQueries?: DocumentNode[];
  redirectToListAfterSubmit?: boolean;
};

export const LoginRuleForm = ({
  refetchQueries,
  onFinish,
  id,
  redirectToListAfterSubmit = true,
}: LoginRuleFormProps) => {
  const { t } = useTranslation();
  const { entity } = useLoginRule({ id: id });
  const [create] = useMutation(CreateLoginRuleDocument, {
    refetchQueries,
  });
  const [update] = useMutation(UpdateLoginRuleDocument, {
    refetchQueries,
  });
  const navigate = useNavigate();
  const [acceptCriteria, setAcceptCriteria] = useState<LoginRuleCriteria[]>([]);

  const [exceptCriteria, setExceptCriteria] = useState<LoginRuleCriteria[]>([]);

  const [selectedRoles, setSelectedRoles] = useState<{
    rolesIncluded: SelectedRole[];
    rolesExcluded: SelectedRole[];
  }>({
    rolesIncluded: [],
    rolesExcluded: [],
  });

  const { control, handleSubmit, reset } = useForm<Form>();

  useEffect(() => {
    if (!entity) {
      return;
    }
    const formData = mapLoginRuleToFormData(entity as any);
    reset(formData);
    setAcceptCriteria(formData.acceptCriteria);
    setExceptCriteria(formData.exceptCriteria);
    setSelectedRoles({
      rolesIncluded: formData.rolesIncluded,
      rolesExcluded: formData.rolesExcluded,
    });
  }, [entity, reset]);

  const onLoginRuleCriteriaAdd = (
    loginRuleCriteriaType: LoginRuleCriteriaType,
    data: LoginRuleCriteria,
  ) => {
    const add = (items: LoginRuleCriteria[]) => [...items, data];

    if (loginRuleCriteriaType === LoginRuleCriteriaType.Include) {
      setAcceptCriteria(add);
    } else if (loginRuleCriteriaType === LoginRuleCriteriaType.Exclude) {
      setExceptCriteria(add);
    }
  };

  const onLoginRuleCriteriaUpdate = (
    loginRuleCriteriaType: LoginRuleCriteriaType,
    data: LoginRuleCriteria,
    index: number,
  ) => {
    const update = (items: LoginRuleCriteria[]) => [
      ...items.slice(0, index),
      data,
      ...items.slice(index + 1),
    ];

    if (loginRuleCriteriaType === LoginRuleCriteriaType.Include) {
      setAcceptCriteria(update);
    } else if (loginRuleCriteriaType === LoginRuleCriteriaType.Exclude) {
      setExceptCriteria(update);
    }
  };

  const onLoginRuleCriteriaDelete = (
    loginRuleCriteriaType: LoginRuleCriteriaType,
    _: LoginRuleCriteria,
    index: number,
  ) => {
    const deleteRuleCriteria = (items: LoginRuleCriteria[]) =>
      items.filter((_, i) => i !== index);

    if (loginRuleCriteriaType === LoginRuleCriteriaType.Include) {
      setAcceptCriteria(deleteRuleCriteria);
    } else if (loginRuleCriteriaType === LoginRuleCriteriaType.Exclude) {
      setExceptCriteria(deleteRuleCriteria);
    }
  };

  const onLoginRuleCriteriaTableActionFinished = (
    loginRuleCriteriaType: LoginRuleCriteriaType,
    action: LoginRuleCriteriaAction,
    { data, index }: { data: LoginRuleCriteria; index: number },
  ) => {
    if (action === LoginRuleCriteriaAction.Edit) {
      onLoginRuleCriteriaUpdate(loginRuleCriteriaType, data, index);
    }
    if (action === LoginRuleCriteriaAction.Delete) {
      onLoginRuleCriteriaDelete(loginRuleCriteriaType, data, index);
    }
  };

  const onSave = async (data: Form) => {
    const entityToUpdate = mapFormDataToLoginRuleDto({
      id: entity?.id,
      form: data,
      acceptCriteria: acceptCriteria,
      exceptCriteria: exceptCriteria,
      rolesIncluded: selectedRoles.rolesIncluded,
      rolesExcluded: selectedRoles.rolesExcluded,
    });
    if (entity?.id) {
      await update({
        variables: {
          id: entity.id,
          data: entityToUpdate as UpdateLoginRuleDto,
          reason: 'default',
        },
      });
    } else {
      await create({
        variables: {
          data: entityToUpdate as CreateLoginRuleDto,
          reason: 'default',
        },
      });
    }

    if (redirectToListAfterSubmit) {
      navigate(Routes.loginRules.path);
    }

    onFinish?.();
  };

  return (
    <ContentWrapper>
      <form onSubmit={handleSubmit(onSave)}>
        <TableWrapper>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="primary.10"
            className="flex items-center uppercase"
          >
            <Rule className="mr-2" />
            {t('login-rules.form.rule-information')}
          </Typography>

          <div className="flex py-2 items-center mt-3">
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              className="min-w-[96px] uppercase"
            >
              {t('login-rules.form.rule-name')}
            </Typography>

            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const value = field?.value;

                return (
                  <Input
                    className="w-full md:w-1/3"
                    {...(field as any)}
                    value={value}
                    defaultValue={value}
                  />
                );
              }}
            />
          </div>
          <div className="flex items-center">
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              className="min-w-[96px] uppercase"
            >
              {t('login-rules.form.rule-description')}
            </Typography>

            <Controller
              name="description"
              control={control}
              rules={{ required: false }}
              render={({ field }) => {
                const value = field?.value;

                return (
                  <Input
                    multiline
                    rows={3}
                    className="w-full md:w-1/3"
                    {...(field as any)}
                    value={value}
                    defaultValue={value}
                  />
                );
              }}
            />
          </div>
        </TableWrapper>

        <TableWrapper className="mt-6">
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="primary.10"
            className="flex items-center mb-4 uppercase"
          >
            <Rule className="mr-3" />
            {t('login-rules.form.applies-to-users-matching')}
          </Typography>

          <LoginRuleCriteriaTable
            data={acceptCriteria}
            options={{
              page: 0,
              count: acceptCriteria.length,
              sort: true,
              rowsPerPage: acceptCriteria.length,
              textLabels: { body: { noMatch: null } },
            }}
            onActionFinished={(...data) =>
              onLoginRuleCriteriaTableActionFinished(
                LoginRuleCriteriaType.Include,
                ...data,
              )
            }
          />

          <AddLoginCriteriaButton
            onSubmit={(...data) =>
              onLoginRuleCriteriaAdd(LoginRuleCriteriaType.Include, ...data)
            }
            className="mt-4"
          />
        </TableWrapper>

        <TableWrapper className="mt-6">
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color="primary.10"
            className="flex items-center mb-4 uppercase"
          >
            <Rule className="mr-3" />
            {t('login-rules.form.except-users-matching')}
          </Typography>

          <LoginRuleCriteriaTable
            data={exceptCriteria}
            options={{
              page: 0,
              count: exceptCriteria.length,
              sort: true,
              rowsPerPage: exceptCriteria.length,
              textLabels: { body: { noMatch: null } },
            }}
            onActionFinished={(...data) =>
              onLoginRuleCriteriaTableActionFinished(
                LoginRuleCriteriaType.Exclude,
                ...data,
              )
            }
          />

          <AddLoginCriteriaButton
            onSubmit={(...data) =>
              onLoginRuleCriteriaAdd(LoginRuleCriteriaType.Exclude, ...data)
            }
            className="mt-4"
          />
        </TableWrapper>

        <TableWrapper className="mt-6">
          <RolesSection
            onChange={setSelectedRoles}
            rolesIncluded={selectedRoles.rolesIncluded}
            rolesExcluded={selectedRoles.rolesExcluded}
          />
        </TableWrapper>

        <Delimiter />
        <div className="flex justify-end space-x-2">
          <Button variant="contained" type="submit">
            {t('login-rules.form.modal.save')}
          </Button>
        </div>
      </form>
    </ContentWrapper>
  );
};

export default LoginRuleForm;
